import {
  HHBox,
  HHChip,
  HHGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { IngestionDetail, SourceResolutionCounts } from '../domain/types';
import { formatCount } from '../utils/format.utils';
import {
  RecordMappingSelection,
  selectRecordMappingCounts,
  toFailureReasonRows,
} from '../utils/record-mapping.utils';
import { SectionTitle, StatRow } from './stat-row';
import { SurfaceCard } from './surface-card';

export const IDENTITY_RESOLUTION_SECTION_TEST_ID = 'pdg-identity-resolution';
export const SOURCE_RESOLUTION_CARD_TEST_ID = 'pdg-source-resolution-card';
export const RECORD_MAPPING_CARD_TEST_ID = 'pdg-record-mapping-card';
export const RECORD_MAPPING_SCOPE_TEST_ID = 'pdg-record-mapping-scope';
export const RECORD_MAPPING_UNAVAILABLE_TEST_ID =
  'pdg-record-mapping-unavailable';
export const RECORD_MAPPING_FAILURE_ROW_TEST_ID_PREFIX =
  'pdg-record-mapping-failure';

export const sourceResolutionStatTestId = (key: string): string =>
  `pdg-source-resolution-${key}`;
export const recordMappingStatTestId = (key: string): string =>
  `pdg-record-mapping-${key}`;
export const recordMappingFailureRowTestId = (reason: string): string =>
  `${RECORD_MAPPING_FAILURE_ROW_TEST_ID_PREFIX}-${reason}`;

export const SOURCE_RESOLUTION_TITLE = 'Source resolution';
export const RECORD_MAPPING_TITLE = 'Record mapping';
export const RECORD_MAPPING_FAILURES_TITLE = 'Failures by reason';
export const RECORD_MAPPING_NO_FAILURES_TEXT = 'No failures.';
export const RECORD_MAPPING_UNAVAILABLE_TEXT =
  'The mapping queue could not be read, so these counts are unknown. They are not zero.';
export const RECORD_MAPPING_FINAL_LABEL = 'Final';
export const RECORD_MAPPING_LIVE_LABEL = 'In flight';

interface SourceResolutionCardProps {
  sourceResolution: SourceResolutionCounts;
}

/**
 * What the pipeline could match to a member before mapping ran. Read
 * together with record mapping: a large quarantine here is the usual
 * explanation for a small mapped count there.
 */
const SourceResolutionCard = ({
  sourceResolution,
}: SourceResolutionCardProps): JSX.Element => (
  <SurfaceCard
    isNested
    paddingX={5}
    paddingY={4}
    data-testid={SOURCE_RESOLUTION_CARD_TEST_ID}
  >
    <HHStack gap={1}>
      <SectionTitle>{SOURCE_RESOLUTION_TITLE}</SectionTitle>
      <HHStack>
        <StatRow
          label="Resolved"
          value={formatCount(sourceResolution.resolved)}
          testId={sourceResolutionStatTestId('resolved')}
        />
        <StatRow
          label="Failed"
          value={formatCount(sourceResolution.failed)}
          testId={sourceResolutionStatTestId('failed')}
        />
        <StatRow
          label="Quarantined"
          value={formatCount(sourceResolution.quarantined.total)}
          testId={sourceResolutionStatTestId('quarantined')}
        />
        <StatRow
          label="No match"
          value={formatCount(sourceResolution.quarantined.noMatch)}
          testId={sourceResolutionStatTestId('noMatch')}
        />
        <StatRow
          label="Ambiguous"
          value={formatCount(sourceResolution.quarantined.ambiguous)}
          testId={sourceResolutionStatTestId('ambiguous')}
        />
      </HHStack>
    </HHStack>
  </SurfaceCard>
);

interface FailureBreakdownProps {
  byFailureReason: Parameters<typeof toFailureReasonRows>[0];
}

const FailureBreakdown = ({
  byFailureReason,
}: FailureBreakdownProps): JSX.Element => {
  const rows = toFailureReasonRows(byFailureReason);

  if (rows.length === 0) {
    return (
      <HHTypography
        hhVariant="variant-bypass"
        variant="body2"
        color="text.secondary"
      >
        {RECORD_MAPPING_NO_FAILURES_TEXT}
      </HHTypography>
    );
  }

  return (
    <HHStack>
      {rows.map(row => (
        <StatRow
          key={row.reason}
          label={row.label}
          value={formatCount(row.count)}
          testId={recordMappingFailureRowTestId(row.reason)}
        />
      ))}
    </HHStack>
  );
};

interface RecordMappingCountsViewProps {
  selection: RecordMappingSelection;
}

/**
 * The counts themselves are deliberately not a live region: the detail page
 * polls every couple of seconds, and announcing four changing numbers on
 * every poll would flood a screen reader. The unavailable notice is the
 * live region instead, because it flips rarely and changes what the absence
 * of numbers means.
 */
const RecordMappingCountsView = ({
  selection,
}: RecordMappingCountsViewProps): JSX.Element => {
  if (selection.isUnavailable) {
    return (
      <HHBox
        role="status"
        aria-live="polite"
        data-testid={RECORD_MAPPING_UNAVAILABLE_TEST_ID}
      >
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
        >
          {RECORD_MAPPING_UNAVAILABLE_TEXT}
        </HHTypography>
      </HHBox>
    );
  }

  return (
    <HHStack>
      <StatRow
        label="Mapped"
        value={formatCount(selection.mapped)}
        testId={recordMappingStatTestId('mapped')}
      />
      <StatRow
        label="Failed"
        value={formatCount(selection.failed)}
        testId={recordMappingStatTestId('failed')}
      />
      <StatRow
        label="Dropped"
        value={formatCount(selection.dropped)}
        testId={recordMappingStatTestId('dropped')}
      />
      {selection.pending !== null && (
        <StatRow
          label="Pending"
          value={formatCount(selection.pending)}
          testId={recordMappingStatTestId('pending')}
        />
      )}
    </HHStack>
  );
};

interface RecordMappingCardProps {
  ingestion: IngestionDetail;
}

/**
 * Identity-resolution outcomes for this file. Which half of the payload is
 * authoritative depends on whether the file has finished, so the card says
 * which one it is showing rather than leaving the reader to guess.
 */
const RecordMappingCard = ({
  ingestion,
}: RecordMappingCardProps): JSX.Element => {
  const selection = selectRecordMappingCounts(ingestion);

  return (
    <SurfaceCard
      isNested
      paddingX={5}
      paddingY={4}
      data-testid={RECORD_MAPPING_CARD_TEST_ID}
    >
      <HHStack gap={1}>
        <HHStack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <SectionTitle>{RECORD_MAPPING_TITLE}</SectionTitle>
          <HHChip
            hhVariant="variant-bypass"
            variant="outlined"
            size="small"
            label={
              selection.isFinal
                ? RECORD_MAPPING_FINAL_LABEL
                : RECORD_MAPPING_LIVE_LABEL
            }
            data-testid={RECORD_MAPPING_SCOPE_TEST_ID}
          />
        </HHStack>
        <RecordMappingCountsView selection={selection} />
        {/* Reasons come off the mapping queue, so they exist only on the
            live path. A finished file gets no breakdown rather than a
            "No failures." line that would contradict its failed count. */}
        {!selection.isFinal && !selection.isUnavailable && (
          <HHBox marginTop={2}>
            <HHStack gap={1}>
              <SectionTitle>{RECORD_MAPPING_FAILURES_TITLE}</SectionTitle>
              <FailureBreakdown
                byFailureReason={ingestion.recordMapping.live.byFailureReason}
              />
            </HHStack>
          </HHBox>
        )}
      </HHStack>
    </SurfaceCard>
  );
};

interface IdentityResolutionSectionProps {
  ingestion: IngestionDetail;
}

/**
 * Source resolution and record mapping side by side — the two halves of
 * identity resolution, which an operator reads together when a file lands
 * with fewer members than expected.
 */
export const IdentityResolutionSection = ({
  ingestion,
}: IdentityResolutionSectionProps): JSX.Element => (
  <SurfaceCard
    paddingX={5}
    paddingY={4}
    data-testid={IDENTITY_RESOLUTION_SECTION_TEST_ID}
  >
    <HHStack gap={3}>
      <SectionTitle>Identity resolution</SectionTitle>
      <HHGrid container spacing={4}>
        <HHGrid item xs={12} md={6}>
          <SourceResolutionCard sourceResolution={ingestion.sourceResolution} />
        </HHGrid>
        <HHGrid item xs={12} md={6}>
          <RecordMappingCard ingestion={ingestion} />
        </HHGrid>
      </HHGrid>
    </HHStack>
  </SurfaceCard>
);
